import './styles/TableColor.css';
import { getHalfDonutChart, getScoreChart } from 'containers/Chartjs';
import { dissoc } from 'ramda';
import { Component } from 'react';

import {
  Badge,
  severityToBadgeColor,
} from '@ipfabric/ui-components/Badge/Badge';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { isNonNullable, isNullable } from '@ipfabric/utils';
import LTEBox from 'containers/LTEBox';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import handleEvent from 'utils/handleEvent';
import { getGraphData } from '../../helpers';

function precisionRound(number) {
  return Math.floor(number * 100) / 100;
}

export default class TableColor extends Component {
  static propTypes = {
    absolute: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    Chart: PropTypes.any.isRequired,
    data: PropTypes.shape({
      reportsGroups: PropTypes.array,
      reportsMap: PropTypes.object,
    }).isRequired,
    disableLink: PropTypes.bool,
    displayType: PropTypes.string.isRequired,
    editable: PropTypes.bool,
    group: PropTypes.string,
    msg: PropTypes.object.isRequired,
  };

  static defaultProps = {
    absolute: false,
    disableLink: false,
    editable: false,
  };

  componentDidMount() {
    this.updateGraphData();
  }

  shouldComponentUpdate(nextProps) {
    return this.compareProps(this.props, nextProps);
  }

  getSnapshotBeforeUpdate(prevProps) {
    if (this.compareProps(prevProps, this.props)) {
      if (
        prevProps.displayType === 'stackedBarChart' &&
        this.stackedBarChartInstance
      ) {
        this.stackedBarChartInstance.destroy();
        return 'stackedBarChart';
      }
      if (prevProps.displayType === 'pieChart' && this.pieChartInstance) {
        this.pieChartInstance.destroy();
        return 'pieChart';
      }
    }
    return null;
  }

  componentDidUpdate() {
    this.updateGraphData();
  }

  componentWillUnmount() {
    this.destroyCharts();
  }

  getTitle = (foundGroup) => {
    const { msg } = this.props;
    if (!foundGroup) {
      return get(msg, ['groupSummary', 'groupNotFound']);
    }
    return foundGroup.name;
  };

  getCellValue = (totalResultsPerRow, value) => {
    const { absolute } = this.props;
    if (absolute) {
      return value;
    }
    const result = (value / totalResultsPerRow) * 100;
    // biome-ignore lint/suspicious/noGlobalIsNan: Unsafe to fix in untyped code. Fix after refactoring to typed code.
    return isNaN(result) ? 0 : result;
  };

  compareProps = (prevProps, nextProps) => {
    return (
      nextProps.absolute !== prevProps.absolute ||
      nextProps.group !== prevProps.group ||
      nextProps.displayType !== prevProps.displayType ||
      nextProps.data.reportsto !== prevProps.data.reportsto ||
      nextProps.data.reportsGroups !== prevProps.data.reportsGroups ||
      nextProps.disableLink !== prevProps.disableLink
    );
  };

  updateGraphData = () => {
    const {
      displayType,
      data: { reportsGroups, reportsMap },
      absolute,
    } = this.props;
    if (reportsGroups && reportsMap) {
      if (displayType === 'stackedBarChart') {
        const { msg, Chart, group } = this.props;
        const labels = get(msg, 'labels', {});
        const foundGroup = reportsGroups.find((g) => g.id === group);
        if (foundGroup) {
          const graphData = getGraphData(foundGroup, reportsMap, absolute);
          const title = this.getTitle(foundGroup).toUpperCase();
          this.stackedBarChartInstance = getScoreChart(
            Chart,
            this.stackedBarChart,
            title,
            false,
            labels,
            graphData,
            this.stackedBarChartInstance,
          );
        }
      }
      if (displayType === 'pieChart') {
        const { msg, Chart, group } = this.props;
        const labels = get(msg, 'labels', {});
        const foundGroup =
          isNonNullable(group) && reportsGroups.find((g) => g.id === group);
        if (foundGroup) {
          const graphData = getGraphData(foundGroup, reportsMap);
          const leftTitle = this.getTitle(foundGroup).toUpperCase();
          this.pieChartInstance = getHalfDonutChart(
            Chart,
            this.pieChart,
            leftTitle,
            false,
            labels,
            graphData,
            this.pieChartInstance,
          );
        }
      }
    }
  };

  destroyCharts = () => {
    if (this.stackedBarChartInstance && this.stackedBarChart) {
      this.stackedBarChartInstance.destroy();
      this.stackedBarChartInstance = null;
    }
    if (this.pieChartInstance && this.pieChart) {
      this.pieChartInstance.destroy();
      this.pieChartInstance = null;
    }
  };

  handleTableLinkClick = handleEvent((e) => {
    const { disableLink } = this.props;
    if (disableLink) {
      e.preventDefault();
      return false;
    }
    return true;
  });

  tableNotAvailable = (message) => {
    const { msg } = this.props;
    return (
      <tr key='notAvailable'>
        <td className='dashboard-table-color__td' colSpan='5'>
          {get(msg, message)}
        </td>
      </tr>
    );
  };

  renderTableRows = (foundGroup) => {
    const {
      msg,
      data: { reportsMap, reportsGroups },
      disableLink,
      absolute,
    } = this.props;
    if (!reportsMap || !reportsGroups) {
      return null;
    }
    if (!foundGroup || !foundGroup.children || !foundGroup.children.length) {
      return this.tableNotAvailable('noDataAvailable');
    }
    const maxResults = foundGroup.children.reduce((acc, child) => {
      const childId = child.id;
      const report = reportsMap[childId];
      if (!report) {
        return acc;
      }
      const resultsChecks = get(report, 'result.checks') || {};
      const max = resultsChecks.length;
      return max > acc ? max : acc;
    }, 0);
    const rows = foundGroup.children
      .map((child) => {
        const childId = child.id;
        const report = reportsMap[childId];
        if (!report) {
          return null;
        }
        if (isNullable(report.result) && report.status === -1) {
          return (
            <tr key={`reportError-${childId}`}>
              <td className='dashboard-table-color__td'>{report.name}</td>
              <td className='dashboard-table-color__td' colSpan={maxResults}>
                {get(msg, ['reportError'])}
              </td>
            </tr>
          );
        }
        const checks = get(report, 'result.checks') || {};
        const resultsChecks = Object.keys(checks).reduce((acc, key) => {
          if (isNullable(checks[key])) {
            return dissoc(key, acc);
          }
          return acc;
        }, checks);
        const total = Object.keys(resultsChecks).reduce((acc, key) => {
          const value = resultsChecks[key];
          return acc + value;
        }, 0);
        return (
          <tr key={report.id}>
            <td className='dashboard-table-color__td'>
              {report.name}
              {report.custom && (
                <span className='ml-2'>
                  <Tooltip content='User defined rule'>
                    <i className='fa-fw fa-regular fa-circle-user text-sm' />
                  </Tooltip>
                </span>
              )}
            </td>
            {isNullable(resultsChecks) ? (
              <td className='dashboard-table-color__td' colSpan={4}>
                {get(msg, ['groupSummary', 'noResults'])}
              </td>
            ) : (
              // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
              ['0', '10', '20', '30'].reduce((acc, severity) => {
                const value = resultsChecks[severity];

                if (isNullable(value)) {
                  return acc.concat(
                    <td
                      className='dashboard-table-color__td--empty'
                      key={severity}
                    />,
                  );
                }

                const cellValue = this.getCellValue(total, value);

                return acc.concat(
                  <td
                    className='dashboard-table-color__td dashboard-table-color__td--min'
                    key={severity}
                  >
                    <Tooltip
                      content={get(report, [
                        'descriptions',
                        'checks',
                        severity.toString(),
                      ])}
                    >
                      <span onClick={this.handleTableLinkClick}>
                        <Link
                          to={
                            disableLink
                              ? ''
                              : `${report.webEndpoint}?options={"filters":${JSON.stringify(
                                  {
                                    [report.column]: [
                                      'color',
                                      'eq',
                                      severity.toString(),
                                    ],
                                  },
                                )}}&tmp=1`
                          }
                        >
                          <Badge
                            className='min-w-8'
                            color={severityToBadgeColor[severity]}
                            faded={cellValue === 0}
                          >
                            {(cellValue % 1 === 0
                              ? cellValue
                              : precisionRound(cellValue)
                            ).toLocaleString()}
                            {absolute ? '' : '%'}
                          </Badge>
                        </Link>
                      </span>
                    </Tooltip>
                  </td>,
                );
              }, [])
            )}
          </tr>
        );
      })
      .filter((row) => isNonNullable(row));
    if (!rows || !rows.length) {
      return this.tableNotAvailable('noReportAvailable');
    }
    return rows;
  };

  render() {
    const {
      displayType,
      msg,
      group,
      data: { reportsGroups },
      editable,
    } = this.props;
    if (displayType === 'table') {
      const foundGroup = reportsGroups?.find((g) => g.id === group);
      return (
        <div className='center-block dashboard-table-color__summary-table-row'>
          <LTEBox
            bodyClassName='box-body table-responsive no-padding'
            bodyComponent={
              <table className={`table${editable ? '' : ' table-hover'}`}>
                <thead>
                  <tr>
                    <th>{get(msg, ['groupSummary', 'report'])}</th>
                    <th colSpan={4}>{get(msg, ['groupSummary', 'status'])}</th>
                  </tr>
                </thead>
                <tbody>{this.renderTableRows(foundGroup)}</tbody>
              </table>
            }
            className='box box-primary'
            title={this.getTitle(foundGroup)}
          />
        </div>
      );
    }
    if (displayType === 'stackedBarChart') {
      return (
        <canvas
          className='center-block'
          height='400'
          ref={(c) => {
            this.stackedBarChart = c;
          }}
          width='140'
        />
      );
    }
    if (displayType === 'pieChart') {
      return (
        <canvas
          className='center-block'
          height='270'
          ref={(c) => {
            this.pieChart = c;
          }}
          width='200'
        />
      );
    }
    return null;
  }
}
